/* ──────────────────────────────────────────────────────────────────────────────
   NAMA SEVK BELGESİ — detay + teslimat  ·  /nama-documents/:id  (menü 9551)

   ══ SATICI SÜTAŞ'TIR ══════════════════════════════════════════════════════════
   Ekranın tepesindeki bant bunu söyler ve orada durmasının sebebi var: bu belge
   BİZİM belgemiz değildir. SAP/telsel'de doğar, SAP'de faturalaşır; tüccar plasiyer
   yalnız taşır ve teslim eder. Backoffice BU EKRANDA belgeyi YENİDEN YAZMAZ — yaptığı
   tek şey TESLİMATI KAYDETMEK (düzeltme/masabaşı girişi) ya da belgeyi İPTAL etmek.
   Belge açmak ayrı ekrandır: /nama-documents/new (NamaDocumentCreatePage) — orada da
   satıcı seçilmez, kaynak yalnız SAP/TELSEL olur.

   ══ VARSAYILAN SALT-OKUNUR (#282) ═════════════════════════════════════════════
   Detay izleme modunda açılır; teslim miktarı ancak `?edit=1` (listedeki kalem
   butonu / "Düzenle") ile düzenlenebilir.

   ══ İKİ SERT KURAL (backend de uygular) ═══════════════════════════════════════
   1) Teslim miktarı sipariş miktarını AŞAMAZ — plasiyer olmayan malı teslim edemez.
   2) Teslim < sipariş ise EKSİK GEREKÇESİ ZORUNLUDUR — sebebi kayda geçmeyen eksik
      teslim, sonradan stok sorunu mu müşteri reddi mi ayırt edilemez.
   Client burada da engeller: kullanıcıyı sunucu hatasına yürütmek yerine anında uyarır.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { unwrapLookupItems } from './c360';
import { DetailShell, Section, Grid, useDetailReadOnly, useIsEditIntent } from './detail/DetailShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { fmtDate, fmtDateTime, fmtQty, fmtMoney, unwrap } from './returnShared';
import {
  NAMA_API, statusLabel, statusTone, sourceLabel, sourceStyle, docTypeLabel,
  SHORTAGE_OPTIONS, shortageLabel, isValidShortageReason, isOpen, isClosed,
  sellerNameOf, SELLER_STATEMENT, toNum,
} from './namaShared';

const muted = 'var(--text-secondary, #6b7280)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const st = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: muted, borderBottom: border,
  },
  td: { padding: '9px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))' },
  num: { textAlign: 'right' },
  danger: { color: 'var(--danger, #e2445c)', fontWeight: 800 },
  muted: { color: muted },
  empty: { padding: 12, fontSize: 13, color: muted },
  fact: {
    display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px',
    background: 'var(--bg-card, #fff)', border, borderRadius: 10,
  },
  factLabel: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.2, color: muted },
  factValue: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' },
  // Satıcı bandı — ekranın ilk okunan satırı: bu belge bizim değil.
  sellerBand: {
    display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', marginBottom: 12,
    background: 'linear-gradient(90deg, rgba(37,99,235,0.10), rgba(37,99,235,0.02))',
    border: '1px solid rgba(37,99,235,0.35)', borderRadius: 10,
  },
  qtyInput: {
    width: 92, height: 30, padding: '0 8px', textAlign: 'right', borderRadius: 6,
    border: '1px solid var(--border-color, rgba(15,23,42,0.18))',
    background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #0f172a)',
    fontSize: 13, fontWeight: 700,
  },
  qtyInputBad: { borderColor: 'var(--danger, #e2445c)', outline: 'none' },
  reasonSelect: {
    width: 160, height: 30, padding: '0 6px', borderRadius: 6, fontSize: 12,
    border: '1px solid var(--border-color, rgba(15,23,42,0.18))',
    background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #0f172a)',
  },
  linkBtn: { ...s.btnSecondary, height: 30, padding: '0 10px', fontSize: 12 },
};

function Fact({ label, value, tone }) {
  return (
    <div style={st.fact}>
      <span style={st.factLabel}>{label}</span>
      <span style={{ ...st.factValue, ...(tone === 'bad' ? st.danger : null) }}>{value ?? '-'}</span>
    </div>
  );
}

/** Kalem hatası (client kuralı — backend AYNI kuralı tekrar uygular). */
function lineError(line, entry) {
  const raw = entry?.deliveredQuantity;
  if (raw === '' || raw === undefined || raw === null) return null; // teslim edilmemiş — sorun değil
  const q = toNum(raw);
  const ordered = toNum(line.orderedQuantity);
  if (q < 0) return 'Teslim miktarı negatif olamaz.';
  if (q > ordered) return `Teslim miktarı sipariş miktarını (${fmtQty(ordered)}) aşamaz.`;
  if (q < ordered && !isValidShortageReason(entry?.shortageReason)) return 'Eksik teslimde gerekçe zorunludur.';
  return null;
}

/* Kalemler tablosu — DetailShell'in ReadOnlyContext'i İÇİNDE render edilir, düzenlenebilirliği
   oradan okur (izleme modunda alanlar kilitlidir). */
function LinesTable({ lines, entries, setEntry, errors, onFillAll }) {
  const readOnly = useDetailReadOnly();

  if (!lines.length) return <div style={st.empty}>Belgede kalem yok.</div>;

  const totalOrdered = lines.reduce((a, l) => a + toNum(l.orderedQuantity), 0);
  const totalDelivered = lines.reduce((a, l) => a + toNum(entries[l.id]?.deliveredQuantity), 0);
  const totalAmount = lines.reduce((a, l) => {
    const e = entries[l.id]?.deliveredQuantity;
    const qty = (e === '' || e === undefined || e === null) ? 0 : toNum(e);
    return a + qty * toNum(l.unitPrice);
  }, 0);

  return (
    <>
      {!readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button type="button" style={st.linkBtn} onClick={onFillAll}>Tümünü sipariş miktarıyla doldur</button>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={st.table}>
          <thead>
            <tr>
              <th style={st.th}>Ürün</th>
              <th style={st.th}>Kod</th>
              <th style={st.th}>Birim</th>
              <th style={{ ...st.th, ...st.num }}>Sipariş Miktarı</th>
              <th style={{ ...st.th, ...st.num }}>Teslim Miktarı</th>
              <th style={st.th}>Eksik Gerekçesi</th>
              <th style={{ ...st.th, ...st.num }}>Birim Fiyat</th>
              <th style={{ ...st.th, ...st.num }}>Tutar</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const entry = entries[l.id] || {};
              const err = errors[l.id];
              const raw = entry.deliveredQuantity;
              const hasQty = !(raw === '' || raw === undefined || raw === null);
              const qty = hasQty ? toNum(raw) : 0;
              const short = hasQty && qty < toNum(l.orderedQuantity);
              return (
                <tr key={l.id}>
                  <td style={st.td}>{l.productName || '-'}</td>
                  <td style={st.td}>{l.productCode || '-'}</td>
                  <td style={st.td}>{l.unit || 'AD'}</td>
                  <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(l.orderedQuantity)}</td>
                  <td style={{ ...st.td, ...st.num }}>
                    {readOnly ? (
                      <span style={{ fontWeight: 800 }}>
                        {(l.deliveredQuantity === null || l.deliveredQuantity === undefined)
                          ? '—' : fmtQty(l.deliveredQuantity)}
                      </span>
                    ) : (
                      <input
                        type="text"
                        inputMode="decimal"
                        style={{ ...st.qtyInput, ...(err ? st.qtyInputBad : null) }}
                        value={raw ?? ''}
                        placeholder="—"
                        title={`En fazla ${fmtQty(l.orderedQuantity)} ${l.unit || 'AD'}`}
                        onChange={(e) => {
                          const v = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                          setEntry(l.id, { deliveredQuantity: v });
                        }}
                      />
                    )}
                  </td>
                  <td style={st.td}>
                    {readOnly ? (
                      <span style={st.muted}>{l.shortageReason ? shortageLabel(l.shortageReason) : '—'}</span>
                    ) : (
                      <select
                        style={{
                          ...st.reasonSelect,
                          ...(short && !isValidShortageReason(entry.shortageReason)
                            ? { borderColor: 'var(--danger, #e2445c)' } : null),
                        }}
                        value={entry.shortageReason || ''}
                        disabled={!short}
                        title={short ? 'Eksik teslimde gerekçe zorunludur' : 'Yalnız eksik teslimde doldurulur'}
                        onChange={(e) => setEntry(l.id, { shortageReason: e.target.value })}
                      >
                        {SHORTAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td style={{ ...st.td, ...st.num }}>{fmtMoney(l.unitPrice)}</td>
                  <td style={{ ...st.td, ...st.num, fontWeight: 700 }}>
                    {fmtMoney((hasQty ? qty : toNum(l.orderedQuantity)) * toNum(l.unitPrice))}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...st.td, fontWeight: 800 }} colSpan={3}>Toplam</td>
              <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(totalOrdered)}</td>
              <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(totalDelivered)}</td>
              <td style={st.td} />
              <td style={st.td} />
              <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtMoney(totalAmount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Hatalar kalem kalem gösterilir — "kaydedilemedi" demek, hangi satırın sorunlu olduğunu söylemez. */}
      {Object.keys(errors).length > 0 && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8,
                      background: 'var(--danger-soft, rgba(226, 68, 92,0.08))',
                      border: '1px solid var(--danger, #e2445c)' }}>
          {lines.filter((l) => errors[l.id]).map((l) => (
            <div key={l.id} style={{ fontSize: 12, color: 'var(--danger, #e2445c)', fontWeight: 700 }}>
              {l.productName || l.productCode || 'Kalem'}: {errors[l.id]}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function ConfirmCancelModal({ externalNo, busy, onClose, onSubmit }) {
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>Belgeyi İptal Et{externalNo ? ` — ${externalNo}` : ''}</h3>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={s.modalBody}>
          <p style={{ margin: 0, fontSize: 13, color: muted, lineHeight: 1.6 }}>
            Belge SİLİNMEZ — bu belge Sütaş'ın belgesidir; yalnız İPTAL durumuna alınır.
            Teslimat sırasında düşülmüş araç stoğu geri alınır.
          </p>
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose} disabled={busy}>Vazgeç</button>
          <button style={s.btnDanger} onClick={onSubmit} disabled={busy}>
            {busy ? 'İşleniyor…' : 'Belgeyi İptal Et'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function NamaDocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePageActions();
  const editIntent = useIsEditIntent();
  usePageMeta('Nama Sevk Belgesi', 'Sütaş adına teslim edilen belge — teslim miktarı ve eksik gerekçesi.');

  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [entries, setEntries] = useState({});      // lineId → { deliveredQuantity, shortageReason, batchNo }
  const [delivery, setDelivery] = useState({ warehouseId: '', printedWaybillNo: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Teslim düzenlemesi ancak açık niyet (?edit=1) + Düzenle hakkıyla mümkündür.
  const editing = editIntent && canEdit;

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/warehouses/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([r, w]) => {
      setRepresentatives(unwrapLookupItems(r));
      setWarehouses(unwrapLookupItems(w));
    }).catch(() => { /* lookup yoksa etiketler "-" olur; belge yine açılır */ });
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${NAMA_API}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Nama sevk belgesi alınamadı');
      const d = unwrap(await res.json());
      const h = d?.header || null;
      const ls = d?.lines || [];
      setHeader(h);
      setLines(ls);
      setDelivery({
        warehouseId: h?.warehouseId || '',
        printedWaybillNo: h?.printedWaybillNo || '',
      });
      // Teslim edilmemiş kalem BOŞ gelir (0 değil): 0 "hiç teslim etmedim" demektir ve
      // gerekçe ister; boş ise "henüz dokunulmadı" demektir ve gönderilmez.
      const next = {};
      ls.forEach((l) => {
        next[l.id] = {
          deliveredQuantity: (l.deliveredQuantity === null || l.deliveredQuantity === undefined)
            ? '' : String(l.deliveredQuantity),
          shortageReason: l.shortageReason || '',
          batchNo: l.batchNo || '',
        };
      });
      setEntries(next);
    } catch (e) {
      showToast('error', e.message || 'Nama sevk belgesi alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Müşteri adı: tek kayıt → tek sorgu (müşteri listesi tümüyle indirilmez).
  useEffect(() => {
    if (!header?.customerId) return;
    const url = `/api/customers?$top=1&$filter=${encodeURIComponent(`Id eq ${header.customerId}`)}`;
    fetch(url, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (body) setCustomers(body.value || body.Value || []); })
      .catch(() => {});
  }, [header?.customerId]);

  const setEntry = (lineId, patch) => setEntries((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...patch } }));

  const fillAll = () => {
    setEntries((prev) => {
      const next = { ...prev };
      lines.forEach((l) => {
        next[l.id] = { ...next[l.id], deliveredQuantity: String(l.orderedQuantity ?? 0), shortageReason: '' };
      });
      return next;
    });
  };

  const errors = useMemo(() => {
    const map = {};
    lines.forEach((l) => {
      const err = lineError(l, entries[l.id]);
      if (err) map[l.id] = err;
    });
    return map;
  }, [lines, entries]);

  const customerLabel = customers[0]?.name || customers[0]?.textId || '-';
  const repLabel = representatives.find((x) => x.id === header?.representativeId)?.name || '-';
  const warehouseOptions = useMemo(() => ([
    { value: '', label: 'Temsilcinin satış deposu' },
    ...warehouses.map((w) => ({ value: w.id, label: w.name || w.textId || '(adsız)' })),
  ]), [warehouses]);

  const closed = header && isClosed(header.status);
  const open = header && isOpen(header.status);

  const save = async () => {
    if (Object.keys(errors).length) {
      showToast('error', 'Kalemlerde hata var — teslim miktarı sipariş miktarını aşamaz, eksik teslimde gerekçe zorunludur.');
      return;
    }
    const items = lines
      .filter((l) => {
        const v = entries[l.id]?.deliveredQuantity;
        return !(v === '' || v === undefined || v === null);
      })
      .map((l) => {
        const e = entries[l.id];
        const q = toNum(e.deliveredQuantity);
        return {
          lineId: l.id,
          deliveredQuantity: q,
          shortageReason: q < toNum(l.orderedQuantity) ? (e.shortageReason || null) : null,
          batchNo: e.batchNo || null,
        };
      });

    if (!items.length) {
      showToast('error', 'Teslim edilen kalem girilmedi.');
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(`${NAMA_API}/${id}/deliver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          warehouseId: delivery.warehouseId || null,
          printedWaybillNo: delivery.printedWaybillNo || null,
          items,
        }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Teslimat kaydedilemedi');
      showToast('success', 'Teslimat kaydedildi.');
      setTimeout(() => navigate('/nama-documents'), 400);   // konvansiyon: kayıt sonrası listeye dön
    } catch (e) {
      showToast('error', e.message || 'Teslimat kaydedilemedi');
    } finally {
      setBusy(false);
    }
  };

  const cancelDocument = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${NAMA_API}/${id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'İptal başarısız');
      showToast('success', 'Belge iptal edildi.');
      setConfirm(false);
      load();
    } catch (e) {
      showToast('error', e.message || 'İptal başarısız');
    } finally {
      setBusy(false);
    }
  };

  const crumb = [
    { label: 'Nama Sevk Belgeleri', to: '/nama-documents' },
    { label: header?.externalNo || 'Detay', current: true },
  ];

  // Düzenleme modu aksiyonları (izleme modunda DetailShell kendi "Kapat + Düzenle"sini çizer).
  const actionButtons = header ? (
    <>
      <button type="button" className="dps-btn" onClick={load} disabled={loading || busy}>Yenile</button>
      {canDelete && open && (
        <button type="button" className="dps-btn" onClick={() => setConfirm(true)} disabled={busy}>İptal Et</button>
      )}
      <button type="button" className="dps-btn" onClick={() => navigate('/nama-documents')} disabled={busy}>Kapat</button>
      <button type="button" className="dps-btn primary" onClick={save}
              disabled={busy || !editing || closed || Object.keys(errors).length > 0}
              title={closed ? 'Belge kapanmış — teslim edilemez.' : undefined}>
        {busy ? 'Kaydediliyor…' : 'Teslimatı Kaydet'}
      </button>
    </>
  ) : null;

  const statusNode = header ? (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <StatusBadge tone={statusTone(header.status)}>{statusLabel(header.status)}</StatusBadge>
      <span style={sourceStyle(header.source)}>{sourceLabel(header.source)}</span>
    </span>
  ) : null;

  return (
    <>
      <DetailShell
        crumb={crumb}
        status={statusNode}
        actions={actionButtons}
        loading={loading}
        readOnly={closed || false}
      >
        {!loading && !header && (
          <Section title="Bulunamadı"><div style={st.empty}>Nama sevk belgesi bulunamadı.</div></Section>
        )}

        {header && (
          <>
            {/* SATICI BANDI — ekranın ilk okunan satırı. */}
            <div style={st.sellerBand}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>🏷️</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                  Satıcı: {sellerNameOf(header)}
                </div>
                <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>
                  {SELLER_STATEMENT} Belge {sourceLabel(header.source)} kaynağından gelir ve orada faturalaşır;
                  tüccar plasiyer yalnız taşır ve teslim eder.
                </div>
              </div>
            </div>

            <Section icon="truck" title="Belge"
                     subtitle="Kaynak sistemin belgesi — alanlar burada yeniden yazılmaz, yalnız teslimat kaydedilir."
                     note={closed
                       ? `Belge KAPANMIŞ (${statusLabel(header.status)}) — teslim edilemez, düzenlenemez.`
                       : 'Teslimatı kaydettiğinizde araç deposundan stok düşer ve belge durumu kalemlerden TÜRETİLİR (tamamı teslim → Teslim Edildi, eksik → Kısmi Teslim).'}>
              <Grid cols={3}>
                <Fact label="Belge No" value={header.externalNo || '-'} />
                <Fact label="Tip" value={docTypeLabel(header.docType)} />
                <Fact label="Kaynak" value={sourceLabel(header.source)} />
                <Fact label="Müşteri" value={customerLabel} />
                <Fact label="Temsilci" value={repLabel} />
                <Fact label="Satıcı" value={sellerNameOf(header)} />
                <Fact label="Durum" value={statusLabel(header.status)} />
                <Fact label="Geliş Tarihi" value={fmtDate(header.receivedAt)} />
                <Fact label="Teslim Tarihi" value={header.deliveredAt ? fmtDateTime(header.deliveredAt) : '—'} />
                <Fact label="Net Tutar" value={fmtMoney(header.netAmount)} />
                <Fact label="Brüt Tutar" value={fmtMoney(header.grossAmount)} />
                <Fact label="Basılı İrsaliye No" value={header.printedWaybillNo || '—'} />
                {header.note && <Fact label="Not" value={header.note} />}
              </Grid>
            </Section>

            <Section icon="box" title="Teslimat"
                     subtitle="Stok BURADAN düşer: depo seçilmezse temsilcinin satış (araç) deposu kullanılır.">
              <Grid cols={2}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={st.factLabel}>Araç Deposu</span>
                  <select
                    style={{ ...s.select, height: 38 }}
                    value={delivery.warehouseId || ''}
                    disabled={!editing || closed}
                    onChange={(e) => setDelivery((p) => ({ ...p, warehouseId: e.target.value }))}
                  >
                    {warehouseOptions.map((o) => <option key={o.value || 'default'} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={st.factLabel}>Basılı İrsaliye No</span>
                  <input
                    style={{ ...s.input, height: 38 }}
                    type="text"
                    value={delivery.printedWaybillNo || ''}
                    placeholder="Sahada basılan irsaliye numarası"
                    disabled={!editing || closed}
                    onChange={(e) => setDelivery((p) => ({ ...p, printedWaybillNo: e.target.value }))}
                  />
                </div>
              </Grid>
            </Section>

            <Section icon="list" title="Kalemler"
                     subtitle="Teslim miktarı sipariş miktarını AŞAMAZ; eksik teslimde gerekçe ZORUNLUDUR."
                     note={editing && !closed
                       ? 'Boş bırakılan kalem "henüz teslim edilmedi" sayılır ve gönderilmez. 0 yazmak "hiç teslim etmedim" demektir ve gerekçe ister.'
                       : undefined}>
              <LinesTable
                lines={lines}
                entries={entries}
                setEntry={setEntry}
                errors={editing && !closed ? errors : {}}
                onFillAll={fillAll}
              />
            </Section>
          </>
        )}
      </DetailShell>

      {confirm && (
        <ConfirmCancelModal
          externalNo={header?.externalNo}
          busy={busy}
          onClose={() => setConfirm(false)}
          onSubmit={cancelDocument}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

export default NamaDocumentDetailPage;
